import * as React from 'react';
import styled from 'styled-components';
import { IBaseComponentProps } from '../types';
import { useChooseRef } from '../../utils';
import { ToolbarOverflowMenu, IToolbarRendererProps, ToolbarOverflowCarousel, ToolbarOverflowWrap } from './variants';
import { IToolbarProps } from './types';
import { FlexBox } from '../flex-box';
import { selectThemeSemantics } from '../../styling';

interface IToolbarBaseProps extends
    IToolbarProps, IBaseComponentProps<HTMLDivElement>,
    Pick<IToolbarRendererProps, 'primaryToolbarItems' | 'farPositionedToolbarItems'> { }

const Separator = styled.span.attrs(() => ({
    className: 'ap-toolbar-separator'
})) <Pick<IToolbarProps, 'height'>>`
    flex-shrink: 0;
    width: ${selectThemeSemantics(({ gap }) => gap.xs)};
    height: ${({ height }) => (height ? `${height / 2}px` : '50%')};
    background-color: ${({ theme: { toolbar } }) => toolbar.colors.separatorBackgroundColor};
`;

const ToolbarBase = React.memo((props: IToolbarBaseProps) => {
    const {
        primaryToolbarItems, className, elementRef, maxPrimaryItems, farPositionedToolbarItems, iconSize = '16',
        iconPosition, overflow, useSeparator, separatorGap = '0', height = 40
    } = props;

    const containerRef = useChooseRef(elementRef);
    const component = (overflow === 'menu' ? ToolbarOverflowMenu : (overflow === 'carousel' ? ToolbarOverflowCarousel : ToolbarOverflowWrap));
    const onRenderSeparator = React.useCallback(() => (
        <Separator height={height} />
    ), [height]);

    const toolbarProps: IToolbarRendererProps = React.useMemo(() => ({
        primaryToolbarItems,
        containerRef,
        maxPrimaryItems,
        iconSize,
        iconPosition,
        farPositionedToolbarItems,
        separator: useSeparator ? onRenderSeparator : undefined,
        separatorGap,
        height
    }), [primaryToolbarItems, containerRef, maxPrimaryItems, iconSize, iconPosition, farPositionedToolbarItems, useSeparator, onRenderSeparator, separatorGap, height]);

    return (
        <FlexBox className={className} ref={containerRef} alignItems="center" justifyContent="space-between">
            {React.createElement(component, toolbarProps)}
        </FlexBox>
    );
});

ToolbarBase.displayName = 'Toolbar';

export const Toolbar = styled(ToolbarBase).attrs(() => ({
    className: String('ap-toolbar')
}))`
    height: ${({ height, overflow }) => (overflow === 'wrap' ? 'auto' : height ? `${height}px` : '40px')};
    background: ${({ theme: { toolbar } }) => toolbar.colors.backgroundColor};
    box-shadow: ${({ theme: { toolbar } }) => toolbar.composites.boxShadow};
`;
